import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Box, IconButton } from '@mui/material';
import TablePagination from '@mui/material/TablePagination';
import { CustomStylePagination } from '../Styles';
import { VIEW_TYPE } from '../../enums/ViewType';
import { BackArrowIcon, DownArrowIcon, NextArrowIcon } from '../buttons/IconButtons';
import {debounce} from "lodash";
import {DEBOUNCE_DELAY} from "../../Apis/functions";
import {DEFAULT_PAGINATION_CARD_SIZE, DEFAULT_PAGINATION_LIST_SIZE, DEFAULT_PAGINATION_START} from "./Model/TableModel";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer - IBL
 * @Email : kpozos@ibl.mx
 *
 * @Description Input field (text, number)
 * @param String view: Options: grid/list (optional)
 * @param Object content: Object with variable: totalElements  (required)
 * @param String labelRowsPerPage: Text for the label (required)
 * @param Boolean fixed: If fixed === true, enable fixed style else disabled fixed style (optional)
 * @param Boolean hasContainer: If hasContainer === true, remove margin bottom 16px (optional)
 * @returns Object
 */
const CustomPagination = ({
    view = VIEW_TYPE.GRID.name,
    content,
    labelRowsPerPage,
    fixed = true,
    hasContainer = false,
    searchElement,
    finalArray
}) => {
    const { t } = useTranslation();
    const [page, setPage] = useState(DEFAULT_PAGINATION_START);
    const [rowsPerPage, setRowsPerPage] = useState(view === VIEW_TYPE.GRID.name ? DEFAULT_PAGINATION_CARD_SIZE : DEFAULT_PAGINATION_LIST_SIZE);
    const [paginationModified, setPaginationModified] = useState(false);

    useEffect(() => {
        setPage(DEFAULT_PAGINATION_START);
    }, [finalArray]);

    useEffect(() => {
        if (searchElement !== "") {
            const debounced = debounce(() => setPage(DEFAULT_PAGINATION_START), DEBOUNCE_DELAY);
            debounced();
            return () => debounced.cancel();
        }

        setPage(DEFAULT_PAGINATION_START);
    }, [searchElement]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
        setPaginationModified(true);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value));
        setPage(DEFAULT_PAGINATION_START);
        setPaginationModified(true);
    };
    const customTablePaginationActions = (props) => {
        const { count, page, rowsPerPage, onPageChange } = props;

        const handleBackButtonClick = (event) => {
            onPageChange(event, page - 1);
        };

        const handleNextButtonClick = (event) => {
            onPageChange(event, page + 1);
        };

        return (
            <div className='ms-3 d-flex flex-shrink-0'>
                <IconButton onClick={handleBackButtonClick} disabled={page === DEFAULT_PAGINATION_START} aria-label='previous page'>
                    <BackArrowIcon />
                </IconButton>
                <IconButton
                    onClick={handleNextButtonClick}
                    disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                    aria-label='next page'
                >
                    <NextArrowIcon />
                </IconButton>
            </div>
        );
    };
    const pagination = () => {
        return (
            content?.totalElements > 0 && (
                <Box sx={CustomStylePagination(hasContainer, fixed)}>
                    <TablePagination
                        component='div'
                        rowsPerPageOptions={view === VIEW_TYPE.GRID.name ? [8, 16, 24, 32] : [20, 40, 60]}
                        count={content?.totalElements}
                        page={page}
                        onPageChange={handleChangePage}
                        labelRowsPerPage={t(labelRowsPerPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        className='custom-pagination'
                        actionsComponent={customTablePaginationActions}
                        slotProps={{
                            select: {
                                IconComponent: DownArrowIcon,
                            },
                        }}
                    />
                </Box>
            )
        );
    };

    return {
        pagination,
        page,
        setPage,
        rowsPerPage,
        setRowsPerPage,
        paginationModified,
        setPaginationModified
    };
};
export default CustomPagination;
